import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import FileViewerDialog from "@/components/discord/FileViewerDialog";
import PendingReviewRow from "@/components/discord/PendingReviewRow";
import ClassifyRulesTab from "@/components/discord/ClassifyRulesTab";
import SamplesTab from "@/components/discord/SamplesTab";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  MessageSquare,
  Wifi,
  WifiOff,
  Loader2,
  RefreshCw,
  Plus,
  Trash2,
  Settings,
  FileText,
  Image,
  Hash,
  CheckCircle2,
  XCircle,
  Zap,
  ArrowRight,
  Download,
  RotateCw,
  AlertTriangle,
  Sparkles,
  Sliders,
  Eraser,
} from "lucide-react";

export default function DiscordPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [settingUp, setSettingUp] = useState(false);
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [summary, setSummary] = useState(null);

  // Add channel dialog
  const [addOpen, setAddOpen] = useState(false);
  const [newChannel, setNewChannel] = useState({ discord_channel_id: "", channel_name: "", mapped_module: "general" });
  const [addingSaving, setAddingSaving] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/status");
      setStatus(data);
    } catch (err) {
      console.error("Discord status error", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChannels = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/channels");
      setChannels(data.channels || []);
    } catch { /* ignore */ }
  }, []);

  const fetchMessages = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/messages?limit=20");
      setMessages(data.messages || []);
    } catch { /* ignore */ }
  }, []);

  const fetchAttachments = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/attachments?limit=20");
      setAttachments(data.attachments || []);
    } catch { /* ignore */ }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/summary");
      setSummary(data);
    } catch { /* ignore */ }
  }, []);

  const [diagnose, setDiagnose] = useState(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [viewerAttachment, setViewerAttachment] = useState(null);
  const [importHistory, setImportHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("files");
  const [activeTopTab, setActiveTopTab] = useState("sync");  // sync | rules

  const fetchImportHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/accounting-imports?limit=100");
      setImportHistory(data.imports || []);
    } catch { /* silent */ }
  }, []);

  // Pending review: file AI không chắc chắn → CEO xác nhận module
  const [pendingReviews, setPendingReviews] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  // Closed-loop: dialog "Lưu thành mẫu AI?" sau khi confirm pending_review thành công
  const [saveSamplePrompt, setSaveSamplePrompt] = useState(null);  // {attachment_id, target_module, sheet_type, file_name}

  const fetchPendingReviews = useCallback(async () => {
    setPendingLoading(true);
    try {
      const { data } = await api.get("/discord/accounting-imports/pending-review?limit=100");
      setPendingReviews(data.items || []);
    } catch {
      /* silent */
    } finally {
      setPendingLoading(false);
    }
  }, []);

  const runDiagnose = useCallback(async (silent = false) => {
    setDiagnosing(true);
    try {
      const { data } = await api.get("/discord/diagnose");
      setDiagnose(data);
      if (!silent) {
        if (data.ok) toast.success(data.summary);
        else toast.warning(data.summary);
      }
    } catch (err) {
      if (!silent) toast.error(err.response?.data?.detail || "Lỗi chẩn đoán");
    } finally {
      setDiagnosing(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    fetchChannels();
    fetchMessages();
    fetchAttachments();
    fetchSummary();
    fetchImportHistory();
    fetchPendingReviews();
    runDiagnose(true);
  }, [fetchStatus, fetchChannels, fetchMessages, fetchAttachments, fetchSummary, fetchImportHistory, fetchPendingReviews, runDiagnose]);

  const handleTestConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/discord/test-connection");
      setTestResult(data);
      toast.success(`Bot "${data.bot_name}" kết nối thành công!`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Lỗi kết nối";
      setTestResult({ status: "error", message: msg });
      toast.error(msg);
    } finally {
      setTesting(false);
    }
  };

  const handleSetupChannels = async () => {
    setSettingUp(true);
    try {
      const { data } = await api.post("/discord/setup-channels");
      toast.success(data.message);
      fetchChannels();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi setup channels");
    } finally {
      setSettingUp(false);
    }
  };

  const handleAddChannel = async () => {
    if (!newChannel.discord_channel_id || !newChannel.channel_name) {
      toast.error("Vui lòng nhập Channel ID và tên");
      return;
    }
    setAddingSaving(true);
    try {
      await api.post("/discord/channels", newChannel);
      toast.success("Đã thêm channel mapping");
      setAddOpen(false);
      setNewChannel({ discord_channel_id: "", channel_name: "", mapped_module: "general" });
      fetchChannels();
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi thêm channel");
    } finally {
      setAddingSaving(false);
    }
  };

  const handleDeleteChannel = async (id) => {
    if (!window.confirm("Xoá channel mapping này?")) return;
    try {
      await api.delete(`/discord/channels/${id}`);
      toast.success("Đã xoá");
      fetchChannels();
      fetchStatus();
    } catch (err) {
      toast.error("Lỗi xoá channel");
    }
  };

  const handleToggleChannel = async (id) => {
    try {
      const { data } = await api.put(`/discord/channels/${id}/toggle`);
      toast.success(data.message);
      fetchChannels();
    } catch (err) {
      toast.error("Lỗi toggle");
    }
  };

  const handleToggleAutoImport = async (id) => {
    try {
      const { data } = await api.put(`/discord/channels/${id}/auto-import`);
      toast.success(data.auto_import_accounting ? "Đã bật auto-import Kế toán" : "Đã tắt auto-import");
      fetchChannels();
    } catch (err) {
      toast.error("Lỗi toggle auto-import");
    }
  };

  const handleForceImport = async (attId) => {
    if (!window.confirm("Ghi đè import cũ của file này?")) return;
    try {
      const { data } = await api.post(`/discord/attachments/${attId}/import`, { force: true });
      if (data.status === "success") toast.success(`Đã import → ${data.target_module}`);
      else if (data.status === "skipped") toast.warning(data.error || "Tên file không khớp rule");
      else if (data.status === "duplicate") toast.warning("Duplicate — cần force=true");
      else toast.error(data.error || "Lỗi import");
      fetchAttachments();
      fetchImportHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi import");
    }
  };

  const handleRunAutoImportBatch = async () => {
    if (autoImportRunning) {
      toast.info("Đang có tác vụ chạy, vui lòng chờ");
      return;
    }
    try {
      await api.post("/discord/accounting-imports/run");
      toast.info("Đã bắt đầu auto-import trong nền...");
      setAutoImportRunning(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi chạy batch");
    }
  };

  const handleConfirmPending = async (importId, targetModule, sheetType, dateOverride) => {
    try {
      const body = { target_module: targetModule };
      if (sheetType) body.sheet_type = sheetType;
      if (dateOverride) body.date = dateOverride;
      const { data } = await api.post(`/discord/accounting-imports/pending-review/${importId}/confirm`, body);
      if (data.status === "success") toast.success(`Đã import → ${data.target_module}`);
      else if (data.status === "error") toast.error(data.error || "Lỗi import");
      else toast.info(data.error || "Đã xử lý");

      // Closed-loop (User choice 6a): nếu là ảnh và import success → hỏi lưu thành mẫu
      const item = pendingReviews.find((x) => x.id === importId);
      if (item && data.status === "success") {
        const isImg = (item.mime_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(item.file_name || "");
        if (isImg && item.attachment_id) {
          setSaveSamplePrompt({
            attachment_id: item.attachment_id,
            target_module: targetModule,
            sheet_type: sheetType,
            file_name: item.file_name,
          });
        }
      }

      fetchPendingReviews();
      fetchAttachments();
      fetchImportHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xác nhận");
    }
  };

  // Multi-select & bulk confirm Pending Review
  const togglePendingSelect = (id) => {
    setSelectedPendingIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };
  const selectAllPending = () => {
    const ids = (pendingReviews || []).map((p) => p.id);
    setSelectedPendingIds(ids);
  };
  const clearPendingSelection = () => setSelectedPendingIds([]);

  const handleBulkConfirmSelected = async () => {
    if (selectedPendingIds.length === 0) {
      toast.warning("Chưa chọn file nào");
      return;
    }
    if (!window.confirm(`Xác nhận import ${selectedPendingIds.length} file đã chọn?\nHệ thống sẽ dùng gợi ý của AI (module/date) cho mỗi file.`)) return;
    setBulkConfirming(true);
    try {
      const items = selectedPendingIds.map((id) => ({ import_id: id }));
      await api.post("/discord/accounting-imports/pending-review/bulk-confirm", { items });
      toast.info(`Đã bắt đầu import ${items.length} file trong nền...`);
      setAutoImportRunning(true);
      setSelectedPendingIds([]);
    } catch (err) {
      setBulkConfirming(false);
      toast.error(err.response?.data?.detail || "Lỗi bulk confirm");
    }
  };

  const handleConfirmAllPending = async () => {
    if (!pendingReviews || pendingReviews.length === 0) {
      toast.info("Không có file chờ duyệt");
      return;
    }
    if (!window.confirm(
      `Xác nhận TẤT CẢ ${pendingReviews.length} file đang chờ duyệt?\n` +
      `Hệ thống sẽ dùng gợi ý module của AI cho mỗi file. File nào AI chưa gợi ý sẽ bị bỏ qua.`
    )) return;
    setBulkConfirming(true);
    try {
      await api.post("/discord/accounting-imports/pending-review/bulk-confirm", { confirm_all: true });
      toast.info(`Đã bắt đầu import tất cả file trong nền...`);
      setAutoImportRunning(true);
      setSelectedPendingIds([]);
    } catch (err) {
      setBulkConfirming(false);
      toast.error(err.response?.data?.detail || "Lỗi confirm all");
    }
  };

  const _handleConfirmPendingOld = async (importId, targetModule, sheetType, dateOverride) => {
    try {
      const body = { target_module: targetModule };
      if (sheetType) body.sheet_type = sheetType;
      if (dateOverride) body.date = dateOverride;
      const { data } = await api.post(`/discord/accounting-imports/pending-review/${importId}/confirm`, body);
      if (data.status === "success") toast.success(`Đã import → ${data.target_module}`);
      else if (data.status === "error") toast.error(data.error || "Lỗi import");
      else toast.info(data.error || "Đã xử lý");

      // Closed-loop (User choice 6a): nếu là ảnh và import success → hỏi lưu thành mẫu
      const item = pendingReviews.find((x) => x.id === importId);
      if (item && data.status === "success") {
        const isImg = (item.mime_type || "").startsWith("image/") || /\.(png|jpe?g|gif|webp)$/i.test(item.file_name || "");
        if (isImg && item.attachment_id) {
          setSaveSamplePrompt({
            attachment_id: item.attachment_id,
            target_module: targetModule,
            sheet_type: sheetType,
            file_name: item.file_name,
          });
        }
      }

      fetchPendingReviews();
      fetchAttachments();
      fetchImportHistory();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xác nhận");
    }
  };

  const handleSaveAsSample = async () => {
    if (!saveSamplePrompt) return;
    try {
      const fd = new FormData();
      fd.append("target_module", saveSamplePrompt.target_module);
      if (saveSamplePrompt.sheet_type) fd.append("sheet_type", saveSamplePrompt.sheet_type);
      await api.post(`/discord/samples/from-attachment/${saveSamplePrompt.attachment_id}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90000,
      });
      toast.success("Đã lưu thành mẫu AI! Lần sau ảnh tương tự sẽ được tự phân loại.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi lưu mẫu");
    } finally {
      setSaveSamplePrompt(null);
    }
  };

  const handleDismissPending = async (importId) => {
    if (!window.confirm("Bỏ qua file này? File sẽ không được tự động import nữa.")) return;
    try {
      await api.post(`/discord/accounting-imports/pending-review/${importId}/dismiss`);
      toast.success("Đã bỏ qua");
      fetchPendingReviews();
      fetchAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi bỏ qua");
    }
  };

  const handleRetryAttachment = async (attId) => {
    try {
      await api.post(`/discord/attachments/${attId}/retry`);
      toast.success("Đã đặt lại xử lý");
      fetchAttachments();
    } catch { toast.error("Lỗi retry"); }
  };

  const moduleColors = {
    accounting: { bg: "bg-amber-500/10", text: "text-amber-400", border: "border-amber-500/20", label: "Kế toán" },
    media: { bg: "bg-violet-500/10", text: "text-violet-400", border: "border-violet-500/20", label: "Media" },
    tasks: { bg: "bg-sky-500/10", text: "text-sky-400", border: "border-sky-500/20", label: "Báo cáo ca" },
    operations: { bg: "bg-emerald-500/10", text: "text-emerald-400", border: "border-emerald-500/20", label: "Vận hành" },
    general: { bg: "bg-slate-500/10", text: "text-slate-400", border: "border-slate-500/20", label: "Chung" },
  };

  // --- Manual Sync state ---
  const [syncRange, setSyncRange] = useState("7d");
  const [syncCustomFrom, setSyncCustomFrom] = useState("");
  const [syncCustomTo, setSyncCustomTo] = useState("");
  const [syncing, setSyncing] = useState(false);
  const [syncStats, setSyncStats] = useState(null);

  // Auto-import run state (polled realtime)
  const [autoImportRunning, setAutoImportRunning] = useState(false);
  const [autoImportStatus, setAutoImportStatus] = useState(null);
  const [selectedPendingIds, setSelectedPendingIds] = useState([]);
  const [bulkConfirming, setBulkConfirming] = useState(false);

  // Poll auto-import status every 1.5s when running
  useEffect(() => {
    let active = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/discord/accounting-imports/run-status");
        if (!active) return;
        setAutoImportStatus(data);
        if (data.running) {
          setAutoImportRunning(true);
        } else if (autoImportRunning) {
          setAutoImportRunning(false);
          setBulkConfirming(false);
          const total = (data.imported || 0) + (data.skipped || 0) + (data.duplicate || 0) + (data.pending_review || 0) + (data.errors || 0);
          if (total > 0) {
            toast.success(
              `Xong: ${data.imported} ✓ · Skip ${data.skipped} · Trùng ${data.duplicate} · Chờ ${data.pending_review} · Lỗi ${data.errors}`
            );
          } else if (data.error_msg) {
            toast.info(data.error_msg);
          }
          // Refresh affected data
          fetchAttachments?.();
          fetchImportHistory?.();
          fetchPendingReviews?.();
        }
      } catch { /* silent */ }
    };
    // Initial fetch
    poll();
    // Poll periodically
    const id = setInterval(poll, 1500);
    return () => { active = false; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoImportRunning]);

  // Periodic refresh pending-review & import-history khi auto-import đang chạy
  useEffect(() => {
    if (!autoImportRunning) return;
    const id = setInterval(() => {
      fetchPendingReviews?.();
      fetchImportHistory?.();
    }, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoImportRunning]);

  const pollSyncStatus = async () => {
    try {
      const { data } = await api.get("/discord/sync-status");
      setSyncStats(data.stats);
      if (!data.running) {
        setSyncing(false);
        if (data.stats) {
          toast.success(`Sync xong: ${data.stats.messages_new} tin mới, ${data.stats.attachments_new} file, ${data.stats.forwarded_msgs} tin chuyển tiếp`);
          fetchStatus(); fetchMessages(); fetchAttachments();
        }
        return false;
      }
      return true;
    } catch { setSyncing(false); return false; }
  };

  const handleManualSync = async () => {
    if (syncRange === "custom" && !syncCustomFrom) {
      toast.error("Chọn ngày bắt đầu");
      return;
    }
    setSyncing(true);
    try {
      const body = { range: syncRange };
      if (syncRange === "custom") {
        body.custom_from = new Date(syncCustomFrom).toISOString();
        if (syncCustomTo) body.custom_to = new Date(syncCustomTo + "T23:59:59").toISOString();
      }
      await api.post("/discord/sync", body);
      toast.info("Đang sync trong nền...");
      // Poll every 2s
      const poll = setInterval(async () => {
        const stillRunning = await pollSyncStatus();
        if (!stillRunning) clearInterval(poll);
      }, 2000);
    } catch (err) {
      setSyncing(false);
      toast.error(err.response?.data?.detail || "Lỗi sync");
    }
  };

  const handleRefreshUrls = async () => {
    try {
      const { data } = await api.post("/discord/attachments/refresh-urls");
      toast.success(`Đã refresh ${data.refreshed}/${data.total_requested || 0} URL`);
      fetchAttachments();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi refresh");
    }
  };

  // ==================== DISCORD SETTINGS (THRESHOLDS) ====================
  const [settings, setSettings] = useState(null);
  const [savingSettings, setSavingSettings] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await api.get("/discord/settings");
      setSettings(data);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleUpdateSettings = async (field, value) => {
    const newVal = parseFloat(value);
    if (isNaN(newVal) || newVal < 0 || newVal > 1) {
      toast.error("Ngưỡng phải nằm giữa 0.0 và 1.0");
      return;
    }
    setSavingSettings(true);
    try {
      const { data } = await api.put("/discord/settings", { [field]: newVal });
      setSettings((s) => ({ ...s, [field]: newVal, updated_at: new Date().toISOString() }));
      toast.success(data.message || "Đã cập nhật ngưỡng");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi lưu ngưỡng");
    } finally {
      setSavingSettings(false);
    }
  };

  // ==================== WIPE DATA ====================
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeRange, setWipeRange] = useState("7d");
  const [wiping, setWiping] = useState(false);

  const handleWipeData = async () => {
    const rangeLabel = { "1d": "1 ngày qua", "3d": "3 ngày qua", "7d": "7 ngày qua", all: "TOÀN BỘ" }[wipeRange];
    if (!window.confirm(
      `⚠️ XOÁ ${rangeLabel} dữ liệu Discord + các record đã import vào Kế toán?\n\n` +
      `Bao gồm:\n- File sync Discord\n- Lịch sử import\n- Cache OCR\n- Doanh thu Pippy / Doanh thu ngày (OCR)\n- Giao dịch Sổ thu chi (từ Discord)\n\nKhông thể hoàn tác!`
    )) return;
    setWiping(true);
    try {
      const { data } = await api.post("/discord/wipe-data", { range: wipeRange });
      toast.success(data.message);
      setWipeOpen(false);
      fetchStatus(); fetchMessages(); fetchAttachments(); fetchImportHistory(); fetchPendingReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi xoá dữ liệu");
    } finally {
      setWiping(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="discord-loading">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="discord-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-[#F8FAFC] tracking-tight flex items-center gap-2">
            <MessageSquare className="w-7 h-7 text-indigo-400" />
            Discord Integration
          </h1>
          <p className="text-slate-400 text-sm mt-1">Đồng bộ tin nhắn, file từ Discord Server vào hệ thống</p>
        </div>
        <Button
          onClick={() => { fetchStatus(); fetchChannels(); fetchMessages(); fetchAttachments(); fetchSummary(); }}
          variant="outline"
          size="sm"
          className="border-[#334155] text-slate-300 hover:bg-[#334155] gap-1.5"
          data-testid="discord-refresh-btn"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Làm mới
        </Button>
      </div>

      {/* Top Tabs */}
      <div className="flex items-center gap-1 border-b border-[#334155] overflow-x-auto" data-testid="discord-top-tabs">
        <button
          onClick={() => setActiveTopTab("sync")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeTopTab === "sync"
              ? "text-indigo-400 border-indigo-400"
              : "text-slate-500 border-transparent hover:text-slate-300"
          }`}
          data-testid="discord-tab-sync"
        >
          <MessageSquare className="w-3.5 h-3.5 inline mr-1.5" />
          Đồng bộ Discord
        </button>
        <button
          onClick={() => setActiveTopTab("rules")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeTopTab === "rules"
              ? "text-indigo-400 border-indigo-400"
              : "text-slate-500 border-transparent hover:text-slate-300"
          }`}
          data-testid="discord-tab-rules"
        >
          <Settings className="w-3.5 h-3.5 inline mr-1.5" />
          Cấu hình Rule phân loại
        </button>
        <button
          onClick={() => setActiveTopTab("samples")}
          className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap ${
            activeTopTab === "samples"
              ? "text-indigo-400 border-indigo-400"
              : "text-slate-500 border-transparent hover:text-slate-300"
          }`}
          data-testid="discord-tab-samples"
        >
          <Sparkles className="w-3.5 h-3.5 inline mr-1.5" />
          Mẫu ảnh AI
        </button>
      </div>

      {activeTopTab === "rules" ? (
        <ClassifyRulesTab />
      ) : activeTopTab === "samples" ? (
        <SamplesTab />
      ) : (
        <>

      {/* Connection Status Card */}
      <div className={`rounded-xl border p-5 ${
        status?.bot_ready ? "bg-emerald-500/5 border-emerald-500/20" :
        status?.configured ? "bg-indigo-500/5 border-indigo-500/20" :
        "bg-red-500/5 border-red-500/20"
      }`} data-testid="discord-status-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
              status?.bot_ready ? "bg-emerald-500/20" : status?.configured ? "bg-indigo-500/20" : "bg-red-500/20"
            }`}>
              {status?.bot_ready ? (
                <Wifi className="w-6 h-6 text-emerald-400" />
              ) : status?.configured ? (
                <Settings className="w-6 h-6 text-indigo-400 animate-pulse" />
              ) : (
                <WifiOff className="w-6 h-6 text-red-400" />
              )}
            </div>
            <div>
              <h3 className="text-base font-semibold text-[#F8FAFC]">
                {status?.bot_ready ? "Bot đang hoạt động" :
                 status?.configured ? "Bot đã cấu hình — Đang chờ kết nối" :
                 "Chưa cấu hình Discord Bot"}
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Server ID: <span className="text-slate-300 font-mono">{status?.server_id || "—"}</span>
                {status?.last_synced && (
                  <span className="ml-3">Sync lần cuối: <span className="text-slate-300">{new Date(status.last_synced).toLocaleString("vi-VN")}</span></span>
                )}
              </p>
            </div>
          </div>
          <Button
            onClick={handleTestConnection}
            disabled={testing}
            className="bg-indigo-500 hover:bg-indigo-400 text-white font-semibold gap-1.5"
            data-testid="discord-test-connection-btn"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
            {testing ? "Đang kiểm tra..." : "Kiểm tra kết nối"}
          </Button>
        </div>

        {/* Test Result */}
        {testResult && (
          <div className={`mt-4 rounded-lg p-3 ${testResult.status === "connected" ? "bg-emerald-500/10 border border-emerald-500/20" : "bg-red-500/10 border border-red-500/20"}`} data-testid="discord-test-result">
            {testResult.status === "connected" ? (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                  <span className="text-sm text-emerald-400 font-medium">Kết nối thành công — Bot: {testResult.bot_name}</span>
                </div>
                {testResult.server_channels?.length > 0 && (
                  <div className="mt-2">
                    <p className="text-xs text-slate-400 mb-1.5">Các kênh text trên server ({testResult.server_channels.length}):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {testResult.server_channels.map(ch => (
                        <Badge key={ch.id} className="bg-[#334155]/50 text-slate-300 border-[#334155] text-[10px] font-mono">
                          <Hash className="w-2.5 h-2.5 mr-0.5" />{ch.name}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <XCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm text-red-400">{testResult.message || "Lỗi kết nối"}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Diagnose banner — show WARNING if issues, green OK if all good */}
      {diagnose && (
        <div
          className={`rounded-xl border p-4 ${
            diagnose.ok
              ? "border-emerald-500/30 bg-emerald-500/5"
              : diagnose.report?.missing_message_content_intent
              ? "border-red-500/40 bg-red-500/5"
              : "border-amber-500/40 bg-amber-500/5"
          }`}
          data-testid="discord-diagnose-banner"
        >
          <div className="flex items-start gap-3">
            {diagnose.ok ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5 text-emerald-400" />
            ) : (
              <AlertTriangle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                diagnose.report?.missing_message_content_intent ? "text-red-400" : "text-amber-400"
              }`} />
            )}
            <div className="flex-1">
              <h3 className={`text-sm font-bold mb-1 ${
                diagnose.ok ? "text-emerald-300" :
                diagnose.report?.missing_message_content_intent ? "text-red-300" : "text-amber-300"
              }`}>
                {diagnose.summary}
              </h3>
              {diagnose.report?.missing_message_content_intent && (
                <div className="mt-2 space-y-1.5 text-[12px] text-slate-300">
                  <p className="font-semibold">Cách khắc phục (2 bước):</p>
                  <ol className="list-decimal ml-5 space-y-1">
                    <li>
                      Vào{" "}
                      <a
                        href={`https://discord.com/developers/applications/${diagnose.report.app_id}/bot`}
                        target="_blank" rel="noopener noreferrer"
                        className="text-sky-400 hover:text-sky-300 underline font-semibold"
                        data-testid="discord-dev-portal-link"
                      >
                        Discord Developer Portal → Bot
                      </a>{" "}
                      → Scroll xuống <b>Privileged Gateway Intents</b> → Bật toggle{" "}
                      <b className="text-emerald-400">MESSAGE CONTENT INTENT</b> → Save Changes.
                    </li>
                    <li>
                      Quay lại trang này → Click <b>Chẩn đoán lại</b> bên dưới. Sau đó bấm <b>Bắt đầu Sync</b> — file sẽ được lấy về ngay.
                    </li>
                  </ol>
                  <p className="text-[10px] text-slate-500 mt-1">
                    Bot: <code className="bg-slate-800 px-1 py-0.5 rounded">{diagnose.report.bot_name}</code> —
                    app_id: <code className="bg-slate-800 px-1 py-0.5 rounded">{diagnose.report.app_id}</code>
                  </p>
                </div>
              )}
              {!diagnose.report?.missing_message_content_intent && !diagnose.ok && diagnose.issues?.length > 0 && (
                <ul className="mt-1 text-[12px] text-slate-300 list-disc ml-5 space-y-0.5">
                  {diagnose.issues.map((iss, i) => <li key={i}>{iss}</li>)}
                </ul>
              )}
              {diagnose.ok && diagnose.report?.attachments_found > 0 && (
                <p className="text-[11px] text-emerald-300/70 mt-0.5">
                  Bot đã đọc được {diagnose.report.attachments_found} file trong {diagnose.report.channels_accessible} kênh. Click <b>Bắt đầu Sync</b> để lấy về DB.
                </p>
              )}
            </div>
            <Button
              onClick={() => runDiagnose(false)}
              disabled={diagnosing}
              size="sm"
              variant="outline"
              className="border-slate-600 text-slate-300 text-[11px] h-7"
              data-testid="discord-rediagnose-btn"
            >
              {diagnosing ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              <span className="ml-1">Chẩn đoán lại</span>
            </Button>
          </div>
        </div>
      )}

      {/* Manual Sync Panel */}
      <div className="rounded-xl border border-[#334155] bg-gradient-to-br from-indigo-500/5 to-violet-500/5 p-4" data-testid="discord-sync-panel">
        <div className="flex items-center gap-2 mb-3">
          <Zap className="w-4 h-4 text-indigo-400" />
          <h3 className="font-semibold text-slate-200 text-sm">Đồng bộ thủ công</h3>
          <span className="text-[10px] text-slate-500">— lấy lịch sử tin nhắn + file (bao gồm tin chuyển tiếp)</span>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[140px]">
            <label className="text-[10px] text-slate-500 block mb-1">Khoảng thời gian</label>
            <Select value={syncRange} onValueChange={setSyncRange}>
              <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-sm" data-testid="discord-sync-range">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#1e293b] border-[#334155]">
                <SelectItem value="1d" className="text-slate-300">1 ngày qua</SelectItem>
                <SelectItem value="2d" className="text-slate-300">2 ngày qua</SelectItem>
                <SelectItem value="3d" className="text-slate-300">3 ngày qua</SelectItem>
                <SelectItem value="7d" className="text-slate-300">7 ngày qua</SelectItem>
                <SelectItem value="30d" className="text-slate-300">1 tháng qua</SelectItem>
                <SelectItem value="all" className="text-slate-300">Toàn bộ lịch sử</SelectItem>
                <SelectItem value="custom" className="text-slate-300">Tuỳ chọn...</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {syncRange === "custom" && (
            <>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Từ ngày</label>
                <Input type="date" value={syncCustomFrom} onChange={(e) => setSyncCustomFrom(e.target.value)}
                  className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-sm"
                  data-testid="discord-sync-from" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Đến ngày</label>
                <Input type="date" value={syncCustomTo} onChange={(e) => setSyncCustomTo(e.target.value)}
                  className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-sm"
                  data-testid="discord-sync-to" />
              </div>
            </>
          )}
          <Button
            onClick={handleManualSync}
            disabled={syncing || !status?.configured}
            className="bg-indigo-500 hover:bg-indigo-600 text-white h-9 text-sm font-semibold px-4 disabled:opacity-50"
            data-testid="discord-sync-btn"
          >
            {syncing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Đang sync...</> : <><Zap className="w-3.5 h-3.5 mr-1" /> Bắt đầu Sync</>}
          </Button>
          <Button
            onClick={handleRefreshUrls}
            variant="outline"
            className="border-[#334155] text-slate-300 hover:bg-[#334155] h-9 text-sm"
            data-testid="discord-refresh-urls-btn"
            title="Refresh URL file đã hết hạn 24h"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh URL
          </Button>
          <Button
            onClick={() => setWipeOpen(true)}
            variant="outline"
            className="border-red-500/30 text-red-400 hover:bg-red-500/10 h-9 text-sm"
            data-testid="discord-wipe-btn"
            title="Xoá dữ liệu đã sync để đồng bộ lại"
          >
            <Eraser className="w-3.5 h-3.5 mr-1" /> Xoá dữ liệu
          </Button>
        </div>
        {syncStats && (
          <div className="mt-3 space-y-2" data-testid="discord-sync-stats">
            {/* Progress bar khi đang sync */}
            {(syncing || syncStats.phase === "auto_import") && (
              <div className="bg-[#0f172a] border border-indigo-500/30 rounded-lg p-3 space-y-2" data-testid="discord-sync-progress">
                <div className="flex items-center justify-between text-[11px]">
                  <span className="text-indigo-300 font-semibold flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    {syncStats.phase === "auto_import"
                      ? "Giai đoạn 2: Auto-import AI đang xử lý file..."
                      : `Giai đoạn 1: Đang quét kênh ${syncStats.current_channel || "..."}`}
                  </span>
                  <span className="text-slate-400">
                    {syncStats.phase === "auto_import"
                      ? "Đang chạy phân loại AI..."
                      : `${syncStats.channels_done || 0}/${syncStats.channels || 0} kênh`}
                  </span>
                </div>
                <div className="h-1.5 bg-[#334155] rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ${syncStats.phase === "auto_import" ? "bg-sky-400" : "bg-indigo-400"}`}
                    style={{
                      width: syncStats.phase === "auto_import"
                        ? "100%"
                        : `${Math.min(100, ((syncStats.channels_done || 0) / Math.max(1, syncStats.channels || 1)) * 100)}%`
                    }}
                  />
                </div>
                <div className="text-[10px] text-slate-500">
                  Đã quét <span className="text-slate-300 font-semibold">{syncStats.messages_scanned}</span> tin · mới <span className="text-emerald-400 font-semibold">{syncStats.messages_new}</span> · file mới <span className="text-amber-400 font-semibold">{syncStats.attachments_new}</span>
                </div>
              </div>
            )}

            <div className="grid grid-cols-4 gap-2 text-[11px]">
              <div className="bg-[#0f172a] rounded p-2 border border-[#334155]">
                <div className="text-slate-500">Tin quét</div>
                <div className="text-sm font-bold text-slate-200">{syncStats.messages_scanned}</div>
              </div>
              <div className="bg-[#0f172a] rounded p-2 border border-[#334155]">
                <div className="text-slate-500">Tin mới</div>
                <div className="text-sm font-bold text-emerald-400">{syncStats.messages_new}</div>
              </div>
              <div className="bg-[#0f172a] rounded p-2 border border-[#334155]">
                <div className="text-slate-500">File mới</div>
                <div className="text-sm font-bold text-amber-400">{syncStats.attachments_new}</div>
              </div>
              <div className="bg-[#0f172a] rounded p-2 border border-[#334155]">
                <div className="text-slate-500">Tin chuyển tiếp</div>
                <div className="text-sm font-bold text-violet-400">{syncStats.forwarded_msgs}</div>
              </div>
            </div>
            {syncStats.auto_import && !syncing && (
              <div className="bg-sky-500/5 border border-sky-500/20 rounded p-2 text-[10.5px] text-sky-300" data-testid="discord-sync-auto-import-summary">
                Auto-import sau sync: <strong>{syncStats.auto_import.imported}</strong> thành công · {syncStats.auto_import.skipped} bỏ qua · {syncStats.auto_import.duplicate} trùng · {syncStats.auto_import.pending_review} chờ duyệt · {syncStats.auto_import.errors} lỗi
              </div>
            )}
          </div>
        )}
      </div>

      {/* AI Threshold Settings Panel */}
      {settings && (
        <div className="rounded-xl border border-[#334155] bg-gradient-to-br from-emerald-500/5 to-teal-500/5 p-4" data-testid="discord-settings-panel">
          <div className="flex items-center gap-2 mb-3">
            <Sliders className="w-4 h-4 text-emerald-400" />
            <h3 className="font-semibold text-slate-200 text-sm">Ngưỡng tự động phân loại AI</h3>
            <span className="text-[10px] text-slate-500">— chỉnh ngưỡng để cân bằng giữa tự động và kiểm soát tay</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-[#0f172a] rounded-lg border border-[#334155] p-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide">
                  Ngưỡng mẫu ảnh (Sample Matcher)
                </label>
                <span className="text-sm font-bold text-emerald-400" data-testid="discord-threshold-sample-value">
                  {(settings.similarity_threshold_auto * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.similarity_threshold_auto}
                onChange={(e) => setSettings((s) => ({ ...s, similarity_threshold_auto: parseFloat(e.target.value) }))}
                onMouseUp={(e) => handleUpdateSettings("similarity_threshold_auto", e.target.value)}
                onTouchEnd={(e) => handleUpdateSettings("similarity_threshold_auto", e.target.value)}
                disabled={savingSettings}
                className="w-full accent-emerald-500"
                data-testid="discord-threshold-sample-slider"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Ảnh đạt ngưỡng → auto-import theo mẫu. Thấp ngưỡng = import nhiều hơn nhưng dễ sai.
              </p>
            </div>
            <div className="bg-[#0f172a] rounded-lg border border-[#334155] p-3">
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-[11px] text-slate-400 font-semibold uppercase tracking-wide">
                  Ngưỡng AI Vision (Fallback)
                </label>
                <span className="text-sm font-bold text-sky-400" data-testid="discord-threshold-ai-value">
                  {(settings.ai_confidence_threshold * 100).toFixed(0)}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={settings.ai_confidence_threshold}
                onChange={(e) => setSettings((s) => ({ ...s, ai_confidence_threshold: parseFloat(e.target.value) }))}
                onMouseUp={(e) => handleUpdateSettings("ai_confidence_threshold", e.target.value)}
                onTouchEnd={(e) => handleUpdateSettings("ai_confidence_threshold", e.target.value)}
                disabled={savingSettings}
                className="w-full accent-sky-500"
                data-testid="discord-threshold-ai-slider"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                AI tổng quát đạt ngưỡng → auto-import. Thấp hơn → chờ CEO duyệt.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="discord-stat-channels">
          <div className="text-xs text-slate-500 mb-1">Channels mapped</div>
          <div className="text-2xl font-bold text-indigo-400">{status?.channels_mapped || 0}</div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="discord-stat-messages">
          <div className="text-xs text-slate-500 mb-1">Tin nhắn đã sync</div>
          <div className="text-2xl font-bold text-emerald-400">{status?.messages_synced || 0}</div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="discord-stat-files">
          <div className="text-xs text-slate-500 mb-1">Files đã sync</div>
          <div className="text-2xl font-bold text-amber-400">{status?.files_synced || 0}</div>
        </div>
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="discord-stat-today">
          <div className="text-xs text-slate-500 mb-1">Hôm nay</div>
          <div className="text-2xl font-bold text-sky-400">{summary?.total_messages || 0} <span className="text-sm font-normal text-slate-500">tin</span></div>
        </div>
      </div>

      {/* Channel Mapping Section */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-4" data-testid="discord-channel-mapping">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
              <Hash className="w-4 h-4 text-indigo-400" />
              Channel Mapping
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Map kênh Discord sang module hệ thống</p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={handleSetupChannels}
              disabled={settingUp}
              variant="outline"
              size="sm"
              className="border-indigo-500/30 text-indigo-400 hover:bg-indigo-500/10 gap-1.5 text-xs"
              data-testid="discord-setup-channels-btn"
            >
              {settingUp ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Zap className="w-3.5 h-3.5" />}
              Setup mặc định
            </Button>
            <Button
              onClick={() => setAddOpen(true)}
              size="sm"
              className="bg-indigo-500 hover:bg-indigo-400 text-white text-xs gap-1.5"
              data-testid="discord-add-channel-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Thêm channel
            </Button>
          </div>
        </div>

        {/* Channels Table */}
        {channels.length === 0 ? (
          <div className="text-center py-8 border border-dashed border-[#334155] rounded-lg">
            <Hash className="w-8 h-8 text-slate-600 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Chưa có channel nào được mapping</p>
            <p className="text-xs text-slate-600 mt-1">Nhấn "Setup mặc định" để tự động tạo 4 kênh đề xuất</p>
          </div>
        ) : (
          <div className="space-y-2">
            {channels.map(ch => {
              const mod = moduleColors[ch.mapped_module] || moduleColors.general;
              return (
                <div key={ch.id} className={`flex items-center justify-between bg-[#0f172a] border ${mod.border} rounded-lg px-4 py-3`} data-testid={`discord-channel-${ch.id}`}>
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg ${mod.bg} flex items-center justify-center`}>
                      <Hash className={`w-4 h-4 ${mod.text}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-200 font-mono">#{ch.channel_name}</span>
                        <ArrowRight className="w-3 h-3 text-slate-600" />
                        <Badge className={`${mod.bg} ${mod.text} border ${mod.border} text-[10px]`}>{mod.label}</Badge>
                      </div>
                      <p className="text-[10px] text-slate-600 font-mono mt-0.5">ID: {ch.discord_channel_id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* Auto-import toggle for Kế toán channels — đổi từ checkbox sang Switch cho rõ trạng thái */}
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 select-none" title="Tự động phân loại file và đẩy sang module Kế toán khi sync">Auto-import KT</span>
                      <Switch
                        checked={!!ch.auto_import_accounting}
                        onCheckedChange={() => handleToggleAutoImport(ch.id)}
                        className="data-[state=checked]:bg-indigo-500 data-[state=unchecked]:bg-slate-700"
                        data-testid={`discord-auto-import-${ch.id}`}
                      />
                    </div>
                    <div className="w-px h-5 bg-[#334155]" />
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-slate-400 select-none">Kích hoạt</span>
                      <Switch
                        checked={ch.is_active}
                        onCheckedChange={() => handleToggleChannel(ch.id)}
                        className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-slate-700"
                        data-testid={`discord-toggle-${ch.id}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteChannel(ch.id)}
                      className="text-slate-500 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                      data-testid={`discord-delete-${ch.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent Messages & Attachments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Messages */}
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3" data-testid="discord-messages-section">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-emerald-400" />
            Tin nhắn gần đây
            <Badge className="bg-[#334155] text-slate-400 text-[10px] ml-auto">{messages.length}</Badge>
          </h3>
          {messages.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Chưa có tin nhắn nào được sync</p>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {messages.map(msg => {
                const mod = moduleColors[msg.mapped_module] || moduleColors.general;
                return (
                  <div key={msg.id} className="bg-[#0f172a] border border-[#334155]/50 rounded-lg p-3" data-testid={`discord-msg-${msg.id}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-xs font-medium text-slate-300">{msg.author_name}</span>
                      <div className="flex items-center gap-1.5">
                        <Badge className={`${mod.bg} ${mod.text} border ${mod.border} text-[9px]`}>#{msg.channel_name}</Badge>
                        {msg.has_attachments && <Image className="w-3 h-3 text-amber-400" />}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400 line-clamp-2">{msg.content || "(Không có nội dung text)"}</p>
                    <p className="text-[10px] text-slate-600 mt-1">{new Date(msg.synced_at).toLocaleString("vi-VN")}</p>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Attachments */}
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3" data-testid="discord-attachments-section">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <FileText className="w-4 h-4 text-amber-400" />
            Files đã sync
            <Badge className="bg-[#334155] text-slate-400 text-[10px] ml-auto">{attachments.length}</Badge>
          </h3>
          {attachments.length === 0 ? (
            <p className="text-xs text-slate-500 text-center py-6">Chưa có file nào được sync</p>
          ) : (
            <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
              {attachments.map(att => {
                const mod = moduleColors[att.mapped_module] || moduleColors.general;
                const statusColor = att.processing_status === "processed" ? "text-emerald-400" :
                  att.processing_status === "failed" ? "text-red-400" : "text-amber-400";
                const statusLabel = att.processing_status === "processed" ? "Xong" :
                  att.processing_status === "failed" ? "Lỗi" : "Chờ xử lý";
                return (
                  <div key={att.id} className="bg-[#0f172a] border border-[#334155]/50 rounded-lg p-3 hover:border-indigo-500/30 transition-colors" data-testid={`discord-att-${att.id}`}>
                    <div className="flex items-center justify-between">
                      <button
                        onClick={() => setViewerAttachment(att)}
                        className="flex items-center gap-2 min-w-0 flex-1 text-left hover:text-indigo-300 transition-colors"
                        data-testid={`discord-att-view-${att.id}`}
                      >
                        <div className="w-7 h-7 rounded bg-[#334155]/50 flex items-center justify-center shrink-0">
                          {att.file_type?.match(/png|jpg|jpeg|gif|webp/) ? (
                            <Image className="w-3.5 h-3.5 text-violet-400" />
                          ) : att.file_type?.match(/pdf/) ? (
                            <FileText className="w-3.5 h-3.5 text-red-400" />
                          ) : att.file_type?.match(/xls|xlsx|csv/) ? (
                            <FileText className="w-3.5 h-3.5 text-emerald-400" />
                          ) : (
                            <FileText className="w-3.5 h-3.5 text-sky-400" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs text-slate-300 truncate">{att.file_name}</p>
                          <p className="text-[10px] text-slate-600">{att.author_name} · {(att.file_size / 1024).toFixed(0)}KB</p>
                        </div>
                      </button>
                      <div className="flex items-center gap-2 shrink-0">
                        {att.accounting_import_status && (
                          <Badge className={`text-[9px] border ${
                            att.accounting_import_status === "success"
                              ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                              : "bg-red-500/15 text-red-400 border-red-500/30"
                          }`} title={`Đã import → ${att.accounting_target_module || ""}`}>
                            {att.accounting_import_status === "success" ? "Đã import" : "Lỗi import"}
                          </Badge>
                        )}
                        <Badge className={`${mod.bg} ${mod.text} border ${mod.border} text-[9px]`}>{mod.label}</Badge>
                        <span className={`text-[10px] font-medium ${statusColor}`}>{statusLabel}</span>
                        {att.processing_status === "failed" && (
                          <button onClick={() => handleRetryAttachment(att.id)} className="text-slate-500 hover:text-amber-400" title="Thử lại">
                            <RotateCw className="w-3 h-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Daily Summary */}
      {summary && summary.by_channel?.length > 0 && (
        <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3" data-testid="discord-daily-summary">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <Zap className="w-4 h-4 text-lime-400" />
            Tổng kết hôm nay ({summary.date})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {summary.by_channel.map((ch, i) => (
              <div key={i} className="bg-[#0f172a] border border-[#334155] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  <Hash className="w-3 h-3 text-indigo-400" />
                  <span className="text-xs font-medium text-slate-300">#{ch.channel}</span>
                </div>
                <div className="flex gap-3 text-[10px] text-slate-500">
                  <span>{ch.messages} tin nhắn</span>
                  <span>{ch.files} files</span>
                  <span>{ch.authors?.length || 0} người</span>
                </div>
              </div>
            ))}
          </div>
          {summary.processing && (
            <div className="flex gap-4 text-xs pt-2 border-t border-[#334155]/50">
              <span className="text-amber-400">Chờ xử lý: {summary.processing.pending}</span>
              <span className="text-emerald-400">Đã xong: {summary.processing.processed}</span>
              <span className="text-red-400">Lỗi: {summary.processing.failed}</span>
            </div>
          )}
        </div>
      )}

      {/* Add Channel Dialog */}
      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="discord-add-channel-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <Plus className="w-4 h-4 text-indigo-400" />
              Thêm Channel Mapping
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">Discord Channel ID</Label>
              <Input
                value={newChannel.discord_channel_id}
                onChange={e => setNewChannel(c => ({ ...c, discord_channel_id: e.target.value }))}
                placeholder="VD: 1498966303124357261"
                className="bg-[#0f172a] border-[#334155] text-slate-50 font-mono text-sm"
                data-testid="discord-new-channel-id"
              />
              <p className="text-[10px] text-slate-600">Copy ID kênh từ Discord (bật Developer Mode)</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">Tên kênh</Label>
              <Input
                value={newChannel.channel_name}
                onChange={e => setNewChannel(c => ({ ...c, channel_name: e.target.value }))}
                placeholder="VD: ke-toan"
                className="bg-[#0f172a] border-[#334155] text-slate-50 text-sm"
                data-testid="discord-new-channel-name"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-400 text-xs">Map sang Module</Label>
              <Select value={newChannel.mapped_module} onValueChange={v => setNewChannel(c => ({ ...c, mapped_module: v }))}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-50" data-testid="discord-new-channel-module">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="accounting" className="text-slate-300">Kế toán</SelectItem>
                  <SelectItem value="media" className="text-slate-300">Media / Content</SelectItem>
                  <SelectItem value="tasks" className="text-slate-300">Báo cáo ca</SelectItem>
                  <SelectItem value="operations" className="text-slate-300">Vận hành</SelectItem>
                  <SelectItem value="general" className="text-slate-300">Chung</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setAddOpen(false)} className="text-slate-400">Hủy</Button>
            <Button onClick={handleAddChannel} disabled={addingSaving} className="bg-indigo-500 hover:bg-indigo-400 text-white font-semibold" data-testid="discord-save-channel-btn">
              {addingSaving ? "Đang lưu..." : "Thêm channel"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Review Section — CEO xác nhận phân loại cho file AI không chắc chắn */}
      <div className="bg-[#1e293b] border border-amber-500/40 rounded-xl p-5 space-y-3" data-testid="discord-pending-review-section">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-400" />
            File chờ CEO duyệt phân loại
            <Badge className="bg-amber-500/15 text-amber-400 border border-amber-500/30 text-[10px]">{pendingReviews.length}</Badge>
          </h3>
          <Button
            onClick={fetchPendingReviews}
            variant="outline"
            disabled={pendingLoading}
            className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10 h-8 text-xs"
            data-testid="discord-refresh-pending-review"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${pendingLoading ? "animate-spin" : ""}`} /> Làm mới
          </Button>
        </div>
        {pendingReviews.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-6" data-testid="discord-pending-review-empty">
            Không có file nào chờ duyệt. AI đã phân loại tự động các file gần đây.
          </p>
        ) : (
          <>
            <p className="text-[11px] text-slate-500 -mt-1">
              AI không chắc chắn phân loại được những file này. Bạn xem ảnh / mở file, chọn module đích và xác nhận import.
            </p>
            {/* Bulk Toolbar */}
            <div className="flex items-center justify-between gap-2 flex-wrap bg-indigo-500/5 border border-indigo-500/20 rounded-lg px-3 py-2">
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={pendingReviews.length > 0 && selectedPendingIds.length === pendingReviews.length}
                  ref={(el) => {
                    if (el) el.indeterminate = selectedPendingIds.length > 0 && selectedPendingIds.length < pendingReviews.length;
                  }}
                  onChange={() => {
                    if (selectedPendingIds.length === pendingReviews.length) clearPendingSelection();
                    else selectAllPending();
                  }}
                  className="w-4 h-4 accent-indigo-500 cursor-pointer"
                  data-testid="pending-review-select-all-checkbox"
                />
                <span className="text-xs text-slate-300">
                  {selectedPendingIds.length === 0
                    ? `Chọn tất cả (${pendingReviews.length})`
                    : `Đã chọn ${selectedPendingIds.length}/${pendingReviews.length}`}
                </span>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {selectedPendingIds.length > 0 && (
                  <>
                    <Button
                      onClick={clearPendingSelection}
                      variant="outline"
                      size="sm"
                      className="h-7 text-[11px] border-[#334155] text-slate-400 hover:bg-[#334155]"
                      data-testid="pending-review-clear-selection"
                    >
                      Bỏ chọn
                    </Button>
                    <Button
                      onClick={handleBulkConfirmSelected}
                      size="sm"
                      disabled={bulkConfirming || autoImportRunning}
                      className="h-7 text-[11px] bg-indigo-500 hover:bg-indigo-400 text-white disabled:opacity-50"
                      data-testid="pending-review-bulk-confirm-selected"
                    >
                      <CheckCircle2 className="w-3 h-3 mr-1" /> Xác nhận {selectedPendingIds.length} đã chọn
                    </Button>
                  </>
                )}
                <Button
                  onClick={handleConfirmAllPending}
                  size="sm"
                  disabled={bulkConfirming || autoImportRunning || pendingReviews.length === 0}
                  className="h-7 text-[11px] bg-emerald-500 hover:bg-emerald-400 text-white disabled:opacity-50"
                  data-testid="pending-review-confirm-all"
                  title="Dùng gợi ý AI cho tất cả file"
                >
                  {bulkConfirming ? (
                    <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Đang xử lý...</>
                  ) : (
                    <><Zap className="w-3 h-3 mr-1" /> Xác nhận tất cả ({pendingReviews.length})</>
                  )}
                </Button>
              </div>
            </div>

            <div className="space-y-2 max-h-[480px] overflow-y-auto pr-1" data-testid="discord-pending-review-list">
              {pendingReviews.map((item) => (
                <PendingReviewRow
                  key={item.id}
                  item={item}
                  selected={selectedPendingIds.includes(item.id)}
                  onToggleSelect={togglePendingSelect}
                  onConfirm={handleConfirmPending}
                  onDismiss={handleDismissPending}
                  onView={(attId) => {
                    // Tìm attachment doc từ list attachments, nếu không có thì tạo proxy object
                    const att = attachments.find((a) => a.id === attId) || {
                      id: attId,
                      file_name: item.file_name,
                      file_type: (item.file_name || "").split(".").pop(),
                      mime_type: item.mime_type,
                    };
                    setViewerAttachment(att);
                  }}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Import History Section */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-5 space-y-3" data-testid="discord-import-history-section">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <FileText className="w-4 h-4 text-indigo-400" />
            Lịch sử Auto-import Kế toán
            <Badge className="bg-[#334155] text-slate-400 text-[10px]">{importHistory.length}</Badge>
          </h3>
          <Button
            onClick={handleRunAutoImportBatch}
            variant="outline"
            disabled={autoImportRunning}
            className="border-indigo-500/40 text-indigo-400 hover:bg-indigo-500/10 h-8 text-xs disabled:opacity-50"
            data-testid="discord-run-auto-import"
          >
            {autoImportRunning ? (
              <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Đang chạy...</>
            ) : (
              <><Zap className="w-3 h-3 mr-1" /> Chạy Auto-import ngay</>
            )}
          </Button>
        </div>

        {/* Progress Bar Auto-import */}
        {autoImportStatus && (autoImportStatus.running || (autoImportStatus.finished_at && autoImportStatus.total > 0)) && (
          <div
            className={`rounded-lg border p-3 space-y-2 ${
              autoImportStatus.running
                ? "bg-indigo-500/5 border-indigo-500/30"
                : "bg-emerald-500/5 border-emerald-500/30"
            }`}
            data-testid="discord-auto-import-progress"
          >
            <div className="flex items-center justify-between text-[11px]">
              <span
                className={`font-semibold flex items-center gap-1 ${
                  autoImportStatus.running ? "text-indigo-300" : "text-emerald-300"
                }`}
              >
                {autoImportStatus.running ? (
                  <><Loader2 className="w-3 h-3 animate-spin" /> Đang xử lý...</>
                ) : (
                  <><CheckCircle2 className="w-3 h-3" /> Đã xong</>
                )}
              </span>
              <span className="text-slate-400 font-mono">
                {autoImportStatus.done}/{autoImportStatus.total} ({autoImportStatus.percent}%)
              </span>
            </div>
            <div className="h-2 bg-[#334155] rounded-full overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  autoImportStatus.running ? "bg-indigo-400" : "bg-emerald-400"
                }`}
                style={{ width: `${autoImportStatus.percent}%` }}
                data-testid="discord-auto-import-progress-bar"
              />
            </div>
            {autoImportStatus.current_file && autoImportStatus.running && (
              <div
                className="text-[10.5px] text-slate-400 truncate"
                data-testid="discord-auto-import-current-file"
                title={autoImportStatus.current_file}
              >
                <span className="text-slate-500">Đang xử lý:</span>{" "}
                <span className="text-sky-300 font-medium">{autoImportStatus.current_file}</span>
              </div>
            )}
            <div className="grid grid-cols-5 gap-2 text-[10px]">
              <StatChip label="✓" value={autoImportStatus.imported} color="emerald" testid="ai-stat-imported" />
              <StatChip label="Skip" value={autoImportStatus.skipped} color="slate" testid="ai-stat-skipped" />
              <StatChip label="Trùng" value={autoImportStatus.duplicate} color="amber" testid="ai-stat-duplicate" />
              <StatChip label="Chờ" value={autoImportStatus.pending_review} color="orange" testid="ai-stat-pending" />
              <StatChip label="Lỗi" value={autoImportStatus.errors} color="red" testid="ai-stat-errors" />
            </div>
            {autoImportStatus.recent_results && autoImportStatus.recent_results.length > 0 && (
              <details className="text-[10.5px]">
                <summary className="cursor-pointer text-slate-400 hover:text-slate-200" data-testid="ai-recent-results-toggle">
                  Xem {autoImportStatus.recent_results.length} file gần nhất
                </summary>
                <div className="mt-2 space-y-0.5 max-h-[160px] overflow-y-auto">
                  {autoImportStatus.recent_results.slice().reverse().map((r, idx) => (
                    <div key={`${r.attachment_id}-${idx}`} className="flex items-center gap-1.5 text-slate-400">
                      <span
                        className={`w-1.5 h-1.5 rounded-full shrink-0 ${
                          r.status === "success" ? "bg-emerald-400"
                            : r.status === "pending_review" ? "bg-amber-400"
                            : r.status === "duplicate" ? "bg-slate-500"
                            : r.status === "skipped" ? "bg-slate-400"
                            : "bg-red-400"
                        }`}
                      />
                      <span className="truncate flex-1 font-mono">{r.file_name || r.attachment_id.slice(0, 8)}</span>
                      <span className="text-slate-500 text-[9.5px]">{r.target_module || r.status}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
            {autoImportStatus.error_msg && !autoImportStatus.running && (
              <div className="text-[10px] text-amber-400 italic">{autoImportStatus.error_msg}</div>
            )}
          </div>
        )}

        {importHistory.length === 0 ? (
          <p className="text-xs text-slate-500 text-center py-6">
            Chưa có lịch sử import. Bật "Auto-import KT" cho channel kế toán + Sync → file sẽ tự phân loại.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[11px]" data-testid="discord-import-history-table">
              <thead>
                <tr className="text-left text-slate-500 border-b border-[#334155]">
                  <th className="py-1.5 pr-2">Ngày</th>
                  <th className="py-1.5 pr-2">File</th>
                  <th className="py-1.5 pr-2">Module</th>
                  <th className="py-1.5 pr-2">Ngày GD</th>
                  <th className="py-1.5 pr-2">Trích xuất</th>
                  <th className="py-1.5 pr-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {importHistory.map(imp => {
                  const d = new Date(imp.imported_at);
                  const moduleLabel = {
                    "so_thu_chi": imp.sheet_type === "so_quy" ? "Sổ quỹ" : imp.sheet_type === "chung_tu" ? "Chứng từ" : "Sổ thu chi",
                    "bang_ke_tien_mat": "Bảng kê tiền mặt",
                    "bang_ke_doanh_thu": "Bảng kê doanh thu",
                    "bao_cao_kd": "Báo cáo kinh doanh",
                  }[imp.target_module] || imp.target_module;
                  return (
                    <tr key={imp.id} className="border-b border-[#334155]/30 hover:bg-[#0f172a]" data-testid={`import-row-${imp.id}`}>
                      <td className="py-1.5 pr-2 text-slate-400">
                        {`${String(d.getDate()).padStart(2,"0")}/${String(d.getMonth()+1).padStart(2,"0")} ${String(d.getHours()).padStart(2,"0")}:${String(d.getMinutes()).padStart(2,"0")}`}
                      </td>
                      <td className="py-1.5 pr-2 text-slate-300 max-w-[200px] truncate">{imp.file_name}</td>
                      <td className="py-1.5 pr-2">
                        <Badge className="bg-indigo-500/15 text-indigo-400 border border-indigo-500/30 text-[9px]">{moduleLabel}</Badge>
                      </td>
                      <td className="py-1.5 pr-2 text-slate-400">{imp.date_guess || imp.ocr_date || "—"}</td>
                      <td className="py-1.5 pr-2 text-slate-400">
                        {imp.extracted_summary?.tx_count > 0 && <span>{imp.extracted_summary.tx_count} dòng</span>}
                        {imp.extracted_summary?.total > 0 && <span className="ml-1 text-emerald-400">{new Intl.NumberFormat("vi-VN").format(imp.extracted_summary.total)}đ</span>}
                      </td>
                      <td className="py-1.5 pr-2">
                        <Badge className={`text-[9px] border ${
                          imp.status === "success" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/30"
                          : imp.status === "duplicate" ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                          : imp.status === "skipped" ? "bg-slate-500/15 text-slate-400 border-slate-500/30"
                          : "bg-red-500/15 text-red-400 border-red-500/30"
                        }`}>
                          {imp.status === "success" ? "Thành công" : imp.status === "duplicate" ? "Trùng" : imp.status === "skipped" ? "Bỏ qua" : "Lỗi"}
                        </Badge>
                        {imp.error && <p className="text-[9px] text-red-500/70 mt-0.5 max-w-[200px] truncate" title={imp.error}>{imp.error}</p>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* File Viewer Dialog */}
      <FileViewerDialog attachment={viewerAttachment} onClose={() => setViewerAttachment(null)} />

      {/* Closed-loop: dialog "Lưu thành mẫu AI?" sau khi confirm pending */}
      <Dialog open={!!saveSamplePrompt} onOpenChange={(o) => !o && setSaveSamplePrompt(null)}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="save-sample-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] text-base flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              Lưu thành mẫu AI?
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Bạn vừa duyệt 1 ảnh. Hệ thống có thể lưu cấu trúc của ảnh này thành <strong className="text-emerald-400">vân tay AI</strong> —
              từ lần sau, các ảnh có cấu trúc tương tự sẽ được tự động phân loại vào đúng tab này.
            </DialogDescription>
          </DialogHeader>
          {saveSamplePrompt && (
            <div className="bg-[#0f172a] border border-[#334155] rounded-lg p-3 space-y-1.5 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-500">File:</span>
                <span className="text-slate-300 truncate max-w-[260px]">{saveSamplePrompt.file_name}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-500">Tab đích:</span>
                <span className="text-emerald-400">
                  {saveSamplePrompt.target_module}
                  {saveSamplePrompt.sheet_type ? ` · ${saveSamplePrompt.sheet_type}` : ""}
                </span>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setSaveSamplePrompt(null)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
              data-testid="save-sample-skip"
            >
              Bỏ qua
            </Button>
            <Button
              onClick={handleSaveAsSample}
              className="bg-emerald-500 hover:bg-emerald-400 text-white h-8 text-xs"
              data-testid="save-sample-confirm"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" /> Lưu thành mẫu
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Wipe Data Dialog */}
      <Dialog open={wipeOpen} onOpenChange={setWipeOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-md" data-testid="discord-wipe-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-400 text-base flex items-center gap-2">
              <Eraser className="w-4 h-4" />
              Xoá dữ liệu Discord + cascaded Kế toán
            </DialogTitle>
            <DialogDescription className="text-slate-500 text-xs">
              Chọn phạm vi thời gian muốn xoá. Hệ thống sẽ xoá <strong className="text-red-400">tất cả dữ liệu đã import</strong> từ Discord trong khoảng đó để bạn có thể đồng bộ lại từ đầu.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] text-slate-400 mb-1 block">Phạm vi xoá</label>
              <Select value={wipeRange} onValueChange={setWipeRange}>
                <SelectTrigger className="bg-[#0f172a] border-[#334155] text-slate-200 h-9 text-sm" data-testid="discord-wipe-range">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#1e293b] border-[#334155]">
                  <SelectItem value="1d" className="text-slate-300">1 ngày qua</SelectItem>
                  <SelectItem value="3d" className="text-slate-300">3 ngày qua</SelectItem>
                  <SelectItem value="7d" className="text-slate-300">7 ngày qua</SelectItem>
                  <SelectItem value="all" className="text-red-300">⚠️ TOÀN BỘ lịch sử</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-[11px] text-red-300 space-y-1">
              <div className="font-semibold flex items-center gap-1">
                <AlertTriangle className="w-3.5 h-3.5" /> Sẽ xoá các collection sau:
              </div>
              <ul className="list-disc pl-4 space-y-0.5 text-[10.5px]">
                <li>File sync Discord (<code>discord_attachments</code>)</li>
                <li>Tin nhắn Discord (<code>discord_messages</code>)</li>
                <li>Lịch sử auto-import (<code>discord_accounting_imports</code>)</li>
                <li>Cache OCR ảnh (<code>discord_ocr_cache</code>) — xoá luôn toàn bộ</li>
                <li>Doanh thu Pippy / Doanh thu ngày (nguồn OCR)</li>
                <li>Giao dịch Sổ thu chi / Báo cáo (nguồn OCR từ Discord)</li>
              </ul>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setWipeOpen(false)}
              className="border-[#334155] text-slate-300 hover:bg-[#334155] h-8 text-xs"
            >
              Huỷ
            </Button>
            <Button
              onClick={handleWipeData}
              disabled={wiping}
              className="bg-red-500 hover:bg-red-400 text-white h-8 text-xs disabled:opacity-50"
              data-testid="discord-wipe-confirm"
            >
              {wiping ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Đang xoá...</> : <><Eraser className="w-3.5 h-3.5 mr-1" /> Xác nhận xoá</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
        </>
      )}
    </div>
  );
}


function StatChip({ label, value, color, testid }) {
  const palette = {
    emerald: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
    amber: "bg-amber-500/10 text-amber-300 border-amber-500/30",
    orange: "bg-orange-500/10 text-orange-300 border-orange-500/30",
    red: "bg-red-500/10 text-red-300 border-red-500/30",
    slate: "bg-slate-500/10 text-slate-400 border-slate-500/30",
  }[color] || "bg-slate-500/10 text-slate-400 border-slate-500/30";
  return (
    <div
      className={`rounded border px-1.5 py-1 text-center ${palette}`}
      data-testid={testid}
    >
      <div className="text-[9px] uppercase opacity-70">{label}</div>
      <div className="text-[11px] font-bold">{value}</div>
    </div>
  );
}
